// @flow
import React from 'react';
// eslint-disable-next-line import/named
import { type IntlShape } from 'react-intl';

// Utils
import { formatPhoneNumber, obfuscateWebkeyParams } from 'utils/string';
import { getMetaTagContentByName } from 'utils/DOM';

// Components
import { Container, Row, Col } from 'reactstrap';
import ParseHTML from 'components/atoms/ParseHTML';
import Panel from 'components/atoms/Panel';
import Button from 'components/atoms/Button';
import Modal from 'components/atoms/Modal';

// Constants
import {
  tooEarlyErrorInfo,
  notAvailableErrorInfo,
  noAppointmentErrorInfo,
  inProgressErrorInfo,
  ERROR_TYPE,
  SCI_ERROR_ROUTE
} from 'constants/selfCheckIn';
import { NEW_APPT } from 'constants/deeplink';

import noAvailableLogo from './not-available-logo.png';
import checkLogo from './check-logo.png';
import tooEarlyLogo from './too-early-logo.png';

import './ErrorPanel.scss';

const {
  TOO_EARLY,
  ALREADY_IN_PROGRESS,
  NOT_AVAILABLE,
  CANCELLED_OR_PAST
} = ERROR_TYPE;

const ns = 'error-panel';

type Props = {
  errorInfo: Object,
  intl: IntlShape,
  errorType: string,
  showStatusButton: boolean,
  webKey: string,
  statusTrackerURL?: string,
  dealerContact: Object,
  dealerCountryCode: string,
  confirmationKey: string,
  dealerLocale: string,
  reservationId: number
};

export default class ErrorPanel extends React.Component<Props> {
  constructor(props: Props, context: any) {
    super(props, context);
    (this: any).contactDealerModal = React.createRef();
  }

  getButtonModal = (content: any, href: string) => (
    <Button
      buttonTheme={'link'}
      className={`${ns}__modal-btn`}
      href={href}
      target="_parent"
    >
      {content}
    </Button>
  );

  getContactDealerModal = () => {
    const { intl, dealerContact, dealerCountryCode } = this.props;
    if (!dealerContact) {
      return null;
    }
    const { servicePhone, email } = dealerContact;
    const phoneNumber = formatPhoneNumber(servicePhone, dealerCountryCode);
    return (
      <Modal
        ref={(this: any).contactDealerModal}
        showCloseButton={true}
        className={`${ns}__modal`}
        title={() =>
          intl.formatMessage({
            id: 'c8.self.check.in.contact.dealer.modal.contact.dealership',
            defaultMessage: 'Contact Dealership'
          })
        }
      >
        {servicePhone &&
          this.getButtonModal(
            intl.formatMessage(
              {
                id: 'c8.self.check.in.contact.dealer.modal.call.number',
                defaultMessage: `Call ${phoneNumber}`
              },
              { phoneNumber }
            ),
            'tel:' + phoneNumber
          )}
        {servicePhone &&
          this.getButtonModal(
            intl.formatMessage({
              id: 'c8.self.check.in.contact.dealer.modal.send.sms.text',
              defaultMessage: 'Send SMS Text'
            }),
            'sms:' + phoneNumber
          )}
        {email &&
          this.getButtonModal(
            intl.formatMessage({
              id: 'c8.self.check.in.contact.dealer.modal.send.email',
              defaultMessage: 'Send Email'
            }),
            'mailto:' + email
          )}
      </Modal>
    );
  };

  getMakeApptUrl = () => {
    const { webKey, confirmationKey, dealerLocale } = this.props;
    const obfuscatedConfirmationKey = obfuscateWebkeyParams(confirmationKey);
    const environment = getMetaTagContentByName('xtime:environment')
      ? `-${getMetaTagContentByName('xtime:environment')}`
      : '';
    const domain = getMetaTagContentByName('xtime:domain');
    const locale = dealerLocale ? `&locale=${dealerLocale}` : '';
    const deepLink = `?webKey=${webKey}:::::${obfuscatedConfirmationKey}&scope=${NEW_APPT}`;
    return `https://consumer${environment}.${domain}/scheduling/${deepLink}${locale}`;
  };

  getShowStatusButton = () => {
    const { statusTrackerURL, showStatusButton, errorType, intl } = this.props;
    if (
      showStatusButton &&
      errorType !== CANCELLED_OR_PAST &&
      errorType !== TOO_EARLY
    ) {
      return (
        <Row>
          <Col className={'text-center'}>
            <Button
              className={`${ns}__error-button`}
              onClick={() => {
                window.open(statusTrackerURL);
              }}
            >
              {intl.formatMessage({
                id: 'c8.self.check.in.common.check.status',
                defaultMessage: 'CHECK STATUS'
              })}
            </Button>
          </Col>
        </Row>
      );
    }
  };

  triggerTrackSCIErrorPage = () => {
    const { errorType } = this.props;
    let pageTitle;
    switch (errorType) {
      case TOO_EARLY:
        pageTitle = 'Too Early';
        break;
      case ALREADY_IN_PROGRESS:
        pageTitle = 'Already In Progress';
        break;
      case CANCELLED_OR_PAST:
        pageTitle = 'Cancelled Or Past Appt';
        break;
      case NOT_AVAILABLE:
      default:
        pageTitle = 'Generic Error';
        break;
    }

    // @TODO: move to gtm-middlaware.js
    window.dataLayer.push({
      event: 'trackPageview',
      SCI_ERROR_ROUTE,
      pageTitle: pageTitle || SCI_ERROR_ROUTE
    });
  };

  render() {
    const {
      intl,
      errorType,
      showStatusButton,
      errorInfo,
      dealerContact
    } = this.props;
    let selfCheckInErrorInfo = {};
    let imageError = noAvailableLogo;
    if (!errorInfo) {
      switch (errorType) {
        case TOO_EARLY:
          imageError = tooEarlyLogo;
          selfCheckInErrorInfo = tooEarlyErrorInfo;
          break;
        case ALREADY_IN_PROGRESS:
          imageError = checkLogo;
          selfCheckInErrorInfo = inProgressErrorInfo;
          break;
        case CANCELLED_OR_PAST:
          selfCheckInErrorInfo = noAppointmentErrorInfo;
          break;
        case NOT_AVAILABLE:
        default:
          selfCheckInErrorInfo = notAvailableErrorInfo;
          imageError = checkLogo;
          break;
      }
      this.triggerTrackSCIErrorPage();
    }
    const {
      mainErrorMessageId,
      mainErrorMessageDefault,
      errorMessageId,
      errorMessageDefault
    } = errorInfo ? errorInfo : selfCheckInErrorInfo;
    return (
      <Panel className={`${ns} d-flex`}>
        {this.getContactDealerModal()}
        <Container>
          <Row>
            <Col className={`my-5 text-center`}>
              <img className={`${ns}__logo`} src={imageError} alt="" />
            </Col>
          </Row>
          <Row>
            <Col className={`${ns}__sub-title mb-4`}>
              {intl.formatMessage({
                id: mainErrorMessageId,
                defaultMessage: mainErrorMessageDefault
              })}
            </Col>
          </Row>
          <Row>
            <Col className={`${ns}__message-error mb-5`}>
              <ParseHTML
                html={intl.formatMessage({
                  id: errorMessageId,
                  defaultMessage: errorMessageDefault
                })}
              />
            </Col>
          </Row>
          {this.getShowStatusButton()}
          {errorType === CANCELLED_OR_PAST && (
            <Row>
              <Col className={'text-center'}>
                <Button
                  className={`${ns}__error-button`}
                  onClick={() => {
                    window.open(this.getMakeApptUrl());
                  }}
                >
                  {intl.formatMessage({
                    id: 'c8.self.check.in.error.page.check.in.make.appointment',
                    defaultMessage: 'MAKE APPOINTMENT'
                  })}
                </Button>
              </Col>
            </Row>
          )}
          {errorType !== TOO_EARLY &&
            dealerContact &&
            !errorInfo && (
              <Row>
                <Col className={'text-center'}>
                  <Button
                    className={`${ns}__error-button`}
                    buttonTheme={
                      showStatusButton || errorType === CANCELLED_OR_PAST
                        ? 'link'
                        : 'default'
                    }
                    onClick={() =>
                      (this: any).contactDealerModal.current.setShowModal(true)
                    }
                  >
                    {intl.formatMessage({
                      id:
                        'c8.self.check.in.error.panel.button.contact.dealership',
                      defaultMessage: 'CONTACT DEALERSHIP'
                    })}
                  </Button>
                </Col>
              </Row>
            )}
        </Container>
      </Panel>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/atoms/ErrorPanel/ErrorPanel.js